<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable ref="rtable" :columns="columnDefs" :settings="{title: t('topCustomers')}" endpoint="reports/customer/top-customers" @onDataLoaded="onDataLoaded">
      <template #chart>
        <div class="grid grid-cols-2 gap-2">
          <div class="col-span-12 md:col-span-2 chart-card" style="height: 400px">
            <v-chart :option="option" autoresize />
          </div>
        </div>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { ColDef } from 'ag-grid-community'
import { useRoute, useRouter } from 'vue-router'
import { useHTTP } from '@/plugins/http'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'
import { formatMoney } from '@/utils/money'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import { formatPostgresDate } from '@/utils/date'
import User from '@/models/user/User'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart, BarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
} from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'

// i18n
const { t } = useI18n({
  messages: {
    en: {
      name: 'Name',
      topCustomers: 'Top Customers',
      cashCustomer: 'Cash Customer',
      totalSales: 'Total Sales',
      customersByTotalSalesAmount: 'customers by total sales amount'
    },
    ar: {
      name: 'الاسم',
      topCustomers: 'افضل الزبائن',
      cashCustomer: 'زبون نقدي',
      totalSales: 'اجمالي المبيعات',
      customersByTotalSalesAmount: 'customers by total sales amount'
    }
  }
})

// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])

const http = useHTTP()
const route = useRoute()
const router = useRouter()
const alertModal = useAlertModal()

use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  BarChart,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
])

provide(THEME_KEY, 'light')

const option = ref({
  title: {
    text: t('topCustomers'),
    subtext: t('customersByTotalSalesAmount'),
    left: 'left'
  },
  toolbox: {
    feature: {
      saveAsImage: {}
    }
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'shadow'
    }
  },
  legend: {},
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true
  },
  dataZoom: [
    {
      id: 'dataZoomY',
      type: 'slider',
      yAxisIndex: 0,
      filterMode: 'empty',
      startValue: 0,
      endValue: 4,
      zoomLock: true,
      handleSize: 0,
      showDetail: false
    },
    {
      type: 'inside',
      id: 'insideY',
      yAxisIndex: 0,
      startValue: 0,
      endValue: 4,
      zoomOnMouseWheel: false,
      moveOnMouseMove: true,
      moveOnMouseWheel: true
    }
  ],
  xAxis: {
    type: 'value',
    boundaryGap: [0, 0.01]
  },
  yAxis: {
    type: 'category',
    inverse: true,
    data: []
  },
  series: [
    {
      type: 'bar',
      data: []
    }
  ]
})

onBeforeMount(async () => {
  columnDefs.value = [
    {
      headerName: t('name'),
      field: 'name',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      valueFormatter: ({ data }) => {
        if (data.id == '') {
          return t('cashCustomer')
        }
        return `${data.name}`
      }
    },
    {
      headerName: t('totalSales'),
      field: 'total_sales',
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return `${formatMoney(data.total_sales, 'IQD')}`
      }
    },
  ]
})

function onDataLoaded(data: any) {
  const chartData = data.slice(0, 10)
  for (let i = 0; i < chartData.length; i++) {
    if (chartData[i].id == '') {
      chartData[i].name = t('cashCustomer');
    }
  }
  option.value.series[0].data = chartData.slice().map((i: any) => i.total_sales)
  option.value.yAxis.data = chartData.slice().map((i: any) => String(i.name).slice(0, 15))
}
</script>
